import clsx from 'clsx'
import { useCallback, useContext, useEffect } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { DrawerSlideDownAnimation, DrawerSlideUpAnimation } from './ModalSheet'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import ReactModal from 'react-modal'
import { MODAL_TRANSITION_DURATION } from './modalConstants'
import { fadeIn, fadeOut } from 'components/utils/animations'
import ModalBackdrop from './ModalBackdrop'
import { MasterModalRegistrationContext } from 'contexts/MasterModalContext'

const CloseButton = styled(IconButton)`
  position: absolute;
  inset-inline-end: ${rem(16)};
  top: ${rem(16)};
  z-index: 1;
`

const LightboxElement = styled(ReactModal)`
  position: relative;
  overflow: hidden;
  display: grid;
  align-items: center;
  background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
  border-radius: 0;
  box-shadow: ${props => props.theme.shadow.top.medium};
  min-height: ${rem(54)};
  height: 100%;
  width: 100%;

  animation-name: ${DrawerSlideDownAnimation};
  animation-iteration-count: 1;
  animation-duration: 300ms;
  animation-fill-mode: forwards;

  &.is-open {
    animation-name: ${DrawerSlideUpAnimation};
  }

  ${mediaQueryUp.tablet} {
    animation-name: ${fadeOut};

    &.is-open {
      animation-name: ${fadeIn};
    }
  }
`

interface Props extends React.PropsWithChildren {
  className?: string
  onClose?: (event: any) => void
  /**
   * Use this to identify your lightbox when registering with the master modal context
   */
  lightboxId?: string
}

function LightBox(props: Props) {
  const {
    children,
    onClose,
    lightboxId,
  } = props

  const elementContext = useModalElementContext()
  const { registerModal, unregisterModal } = useContext(MasterModalRegistrationContext)
  useEffect(() => {
    if (elementContext?.open) {
      const localId = registerModal(lightboxId)

      return () => {
        // always unregister our modal on unmount
        if (localId) {
          unregisterModal(localId)
        }
      }
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [elementContext?.open, lightboxId])

  const handleClose = useCallback((event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation()
    if (onClose) {
      onClose(event)
    } else {
      elementContext?.resolve()
    }
  }, [elementContext, onClose])

  return <LightboxElement
    className={clsx({ 'is-open': elementContext.open })}
    closeTimeoutMS={MODAL_TRANSITION_DURATION}
    overlayElement={(props, contentElement) => <ModalBackdrop
      {...props}
      mode="default"
      transition={elementContext.open ? 'in' : 'out'}
    >
      {contentElement}
    </ModalBackdrop>}
    isOpen={!!elementContext.open}
    shouldFocusAfterRender={false}
    shouldCloseOnOverlayClick
    shouldCloseOnEsc
    shouldReturnFocusAfterClose={false}
    onRequestClose={handleClose}
    onAfterClose={elementContext.afterClose}
  >
    <CloseButton
      kind="tertiary"
      variant="ghost"
      horizontalOutdent="end"
      onClick={handleClose}
    >
      <LineTimesIcon />
    </CloseButton>

    {children}
  </LightboxElement>
}

export default LightBox
